
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { useTituloFinanceiroBase } from '@/composables/Financeiro/TituloFinanceiroBase';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import Card from '@/core/components/Tela/Card.vue';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import SelecionarTipoDocumentoFinanceiro from '@/components/Cadastros/Financeiro/SelecionarTipoDocumentoFinanceiro.vue';
import SelecionarData from '@/core/components/Tela/SelecionarData.vue';
import SelecionarConta from '@/components/Cadastros/Financeiro/SelecionarConta.vue';
import SelecionarCategoriaPlanoConta from '@/components/Cadastros/PlanosContas/SelecionarCategoriaPlanoConta.vue';
import TituloFinanceiroCentroCusto from '@/components/Financeiro/TitulosFinanceiros/TituloFinanceiroCentroCusto.vue';
import { IDTOTituloFinanceiroCentroCusto } from '@/models/DTO/Financeiro/TitulosFinanceiros/IDTOTituloFinanceiroCentroCusto';
import { ESazonalidade } from '@/core/models/Enumeradores/ESazonalidade';
import { ITipoDocumentoFinanceiro } from '@/models/Entidades/Cadastros/Financeiro/ITipoDocumentoFinanceiro';
import ServicoTipoDocumentoFinanceiro from '@/servicos/Cadastros/Financeiro/ServicoTipoDocumentoFinanceiro';
import { EMeioPagamento } from '@/models/Enumeradores/Cadastros/Financeiro/EMeioPagamento';
import { ETipoTituloFinanceiro } from '@/models/Enumeradores/Financeiro/ETipoTituloFinanceiro';
import ServicoConta from '@/servicos/Cadastros/Financeiro/ServicoConta';
import ServicoPlanoContas from '@/servicos/Cadastros/PlanosContas/ServicoPlanoContas';
import { EStatusTituloFinanceiro } from '@/models/Enumeradores/Financeiro/EStatusTituloFinanceiro';
import ServicoParametros from '@/servicos/MeuSistema/ServicoParametros';
import storeSistema from '@/store/storeSistema';
import { ITituloFinanceiro } from '@/models/Entidades/Financeiro/TitulosFinanceiros/ITituloFinanceiro';

export default defineComponent({
  name: 'DadosContaPagarModal',
  props: {
    visivel: {
      type: Boolean,
    },
    editavel: {
      type: Boolean,
      default: true,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
    dados: {
      type: Object as () => ITituloFinanceiro,
      required: true,
    },
  },
  components: {
    RequisicaoModal,
    Card,
    Icone,
    SelecionarTipoDocumentoFinanceiro,
    SelecionarData,
    SelecionarConta,
    SelecionarCategoriaPlanoConta,
    TituloFinanceiroCentroCusto,
  },
  emits: ['update:operacao', 'update:visivel', 'confirmacao', 'update:codigo', 'update:dados'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherEmpresasComEstrategiaPermissaoDados, verificaConceitoParaApresentarEmpresas,
      preencherPermissoesDados, filtrarPermissaoDadosUsuario,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao,
    } = useModalBase(props, emit);
    const servicoPlanoContas = new ServicoPlanoContas();
    const servicoTipoDocumentoFinanceiro = new ServicoTipoDocumentoFinanceiro();
    servicoPlanoContas.requisicaoSistema();
    servicoTipoDocumentoFinanceiro.requisicaoSistema();
    const servicoConta = new ServicoConta();
    const servicoParametros = new ServicoParametros();
    servicoParametros.requisicaoSistema();
    servicoConta.requisicaoSistema();
    telaBase.identificadorRecurso = 'CONTAS_PAGAR';
    telaBase.identificadorPermissao = 'PER_CONTAS_PAGAR';

    const {
      tituloFinanceiroBase, limparTela, atualizarStatus, atualizarSaldo, tituloPendente, obterDescricaoSazonalidade, atualizarUltimoVencimento, obterMensagemRepeticao, obterCentrosCusto, obterMarcadores, checkRepetir, atualizarSaldoPeloDetalhamento, verificarTipoBoleto,
    } = useTituloFinanceiroBase(props);

    const state = reactive({
      apresentaCentroCusto: true,
    });

    async function atualizarTipoDocumentoFinanceiroSelecionado() {
      if (UtilitarioGeral.valorValido(tituloFinanceiroBase.titulo.codigoTipoDocumentoFinanceiro)) {
        tituloFinanceiroBase.tipoDocumentoFinanceiro = await servicoTipoDocumentoFinanceiro.obter(tituloFinanceiroBase.titulo.codigoTipoDocumentoFinanceiro, tituloFinanceiroBase.titulo.codigoEmpresa);
        if (verificarTipoBoleto(tituloFinanceiroBase.tipoDocumentoFinanceiro.meioPagamento) && (tituloFinanceiroBase.titulo.boleto.numeroDocumento === undefined || tituloFinanceiroBase.titulo.boleto.numeroDocumento === '')) {
          tituloFinanceiroBase.titulo.boleto.numeroDocumento = tituloFinanceiroBase.titulo.numeroTitulo;
        }
      } else {
        tituloFinanceiroBase.tipoDocumentoFinanceiro = {} as ITipoDocumentoFinanceiro;
      }
    }

    async function obterCentrosCustoContaContabil() {
      tituloFinanceiroBase.carregandoCentrosCusto = true;
      if (tituloFinanceiroBase.titulo.codigoPlanoContaCategoria > 0 && !UtilitarioGeral.validaLista(tituloFinanceiroBase.centrosCusto)) {
        const centrosCusto = await servicoPlanoContas.obterCentrosCustos(tituloFinanceiroBase.titulo.codigoPlanoContaCategoria, tituloFinanceiroBase.titulo.codigoEmpresa);
        centrosCusto.forEach((c) => {
          const centroCusto = {} as IDTOTituloFinanceiroCentroCusto;
          centroCusto.codigoCentroCusto = c.codigoCentroCusto;
          centroCusto.descricaoCentroCusto = c.descricaoCentroCusto;
          centroCusto.proporcao = c.proporcao;
          centroCusto.ordem = tituloFinanceiroBase.centrosCusto.length + 1;
          tituloFinanceiroBase.centrosCusto.push(centroCusto);
        });
      }
      tituloFinanceiroBase.carregandoCentrosCusto = false;
    }

    async function salvar() {
      tituloFinanceiroBase.titulo.centrosCusto = obterCentrosCusto();
      tituloFinanceiroBase.titulo.marcadores = obterMarcadores();
      emit('update:dados', tituloFinanceiroBase.titulo);
      emit('confirmacao');
      modalBase.computedVisivel = false;
    }

    function atualizarNumeroBoleto() {
      if (tituloFinanceiroBase.editavel && verificarTipoBoleto(tituloFinanceiroBase.tipoDocumentoFinanceiro.meioPagamento)) {
        tituloFinanceiroBase.titulo.boleto.numeroDocumento = tituloFinanceiroBase.titulo.numeroTitulo;
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      limparTela(ETipoTituloFinanceiro.Pagar);
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, false);
        verificaConceitoParaApresentarEmpresas();
        telaBase.permissaoDados = await filtrarPermissaoDadosUsuario(tituloFinanceiroBase.titulo.codigoEmpresa);
        tituloFinanceiroBase.titulo.codigoConta = await servicoConta.obterCodigoContaPadrao(tituloFinanceiroBase.titulo.codigoEmpresa);
        tituloFinanceiroBase.titulo = UtilitarioGeral.clonarObjeto(props.dados);
        modalBase.textoBotaoSalvar = 'Salvar';
        telaBase.carregando = false;
      }
    });

    return {
      props,
      state,
      tituloFinanceiroBase,
      telaBase,
      modalBase,
      ESazonalidade,
      EPermissaoDados,
      EStatusTituloFinanceiro,
      storeSistema,
      UtilitarioGeral,
      apresentarRetornoRequisicao,
      obterMensagemRepeticao,
      atualizarNumeroBoleto,
      obterDescricaoSazonalidade,
      salvar,
      atualizarUltimoVencimento,
      atualizarStatus,
      atualizarTipoDocumentoFinanceiroSelecionado,
      EMeioPagamento,
      ETipoTituloFinanceiro,
      atualizarSaldo,
      obterCentrosCustoContaContabil,
      checkRepetir,
      tituloPendente,
      atualizarSaldoPeloDetalhamento,
      verificarTipoBoleto,
    };
  },
});
